import { t } from '@lingui/core/macro';
import { isProductionEnvironment } from '@luminovo/commons';
import { GlobalCommandDefinition } from '@luminovo/data-stack';
import { useNavigate } from '@luminovo/design-system';
import { useMutation } from '@tanstack/react-query';
import { BugIcon, ZapIcon } from 'lucide-react';
import { useSnackbar } from 'notistack';
import React from 'react';

import { useTrpc } from '@/shared/data/contexts/TrpcProvider';

const REACT_SCAN_SCRIPT_ID = 'react-scan-script';
const REACT_SCAN_CDN_URL = 'https://unpkg.com/react-scan/dist/auto.global.js';

/** Loads react-scan from the CDN on first use, then toggles it; resolves to whether it is now enabled. */
function toggleReactScan(): Promise<boolean> {
    const win = window as any;

    if (win.reactScan) {
        const current = win.reactScan.getOptions?.()?.enabled ?? true;
        win.reactScan.setOptions({ enabled: !current });
        return Promise.resolve(!current);
    }

    return new Promise((resolve, reject) => {
        if (document.getElementById(REACT_SCAN_SCRIPT_ID)) {
            // Script tag exists but API not ready yet — wait a bit
            setTimeout(() => resolve(true), 500);
            return;
        }

        const script = document.createElement('script');
        script.id = REACT_SCAN_SCRIPT_ID;
        script.src = REACT_SCAN_CDN_URL;
        script.async = true;
        script.onload = () => resolve(true);
        script.onerror = () => reject(new Error('Failed to load react-scan from CDN'));
        document.head.appendChild(script);
    });
}

/** Dev utilities for the command palette, outside production only. */
export function useTestingToolCommands(): GlobalCommandDefinition[] {
    const { enqueueSnackbar } = useSnackbar();
    const navigate = useNavigate();

    const trpc = useTrpc();
    const { mutateAsync: callOpenAI } = useMutation(trpc.api.callOpenAI.mutationOptions());

    if (isProductionEnvironment()) {
        return [];
    }

    const testingToolsGroup = () => t`Testing tools`;
    const debugToolsGroup = () => t`Debug tools`;
    const boltIcon = React.createElement(ZapIcon, { size: 20 });
    const debugIcon = React.createElement(BugIcon, { size: 20 });

    const commands: GlobalCommandDefinition[] = [
        {
            id: 'debug-toggle-react-scan',
            label: () => t`Toggle React Scan`,
            description: () => t`Highlight component re-renders to find performance issues`,
            group: debugToolsGroup,
            icon: debugIcon,
            keywords: ['react', 'scan', 'render', 'performance', 'rerender', 'debug', 'highlight'],
            onSelect: async (ctx) => {
                try {
                    const enabled = await toggleReactScan();
                    enqueueSnackbar(enabled ? 'React Scan enabled' : 'React Scan disabled', {
                        variant: enabled ? 'success' : 'info',
                        persist: false,
                    });
                } catch {
                    enqueueSnackbar('Failed to load React Scan', { variant: 'error', persist: false });
                }
                ctx.close();
            },
        },
        {
            id: 'testing-call-openai',
            label: () => 'Call Azure OpenAI',
            group: testingToolsGroup,
            icon: boltIcon,
            keywords: ['test', 'openai', 'azure', 'ai'],
            onSelect: async () => {
                const result = await callOpenAI({
                    model: 'gpt-4.1-2025-04-14',
                    input: 'Hello, world!',
                });
                enqueueSnackbar(`Azure OpenAI called: ${result}`, {
                    variant: 'success',
                    persist: false,
                });
            },
            closeOnComplete: true,
        },
        {
            id: 'testing-feature-flags',
            label: () => t`Toggle feature flags`,
            group: testingToolsGroup,
            icon: boltIcon,
            keywords: ['feature', 'flag', 'toggle', 'enable', 'disable'],
            onSelect: (ctx) => {
                navigate({ to: '/settings/organization/feature-flags', openInNewTab: ctx.openInNewTab });
                ctx.close();
            },
        },
    ];

    return commands;
}
